import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2 } from "lucide-react";

import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FormGrid, SectionTitle, SelectField, TagsField, TextAreaField, TextField } from "./Fields";

export const emptyCandidate = {
  full_name: "", email: "", phone: "", location: "", current_company: "", current_designation: "",
  total_experience_years: null, skills: [], technologies: [], industry: "Automotive",
  education: [], companies: [], certifications: [], projects: [], current_ctc: "", expected_ctc: "",
  notice_period: "30 days", availability: "After notice period", linkedin: "", portfolio: "",
  github: "", source: "Sourced", recruiter_id: null, tags: [], status: "Active", summary: "",
  in_talent_pool: true,
};

export default function CandidateForm({ open, onOpenChange, candidate, initial, onSaved }) {
  const { meta } = useAuth();
  const [form, setForm] = useState(emptyCandidate);
  const [saving, setSaving] = useState(false);
  const [dups, setDups] = useState([]);

  useEffect(() => {
    if (!open) return;
    setForm({ ...emptyCandidate, ...(candidate || initial || {}) });
    setDups([]);
  }, [open, candidate, initial]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const skillPool = [...(meta?.skill_taxonomy?.[form.industry] || []), ...(meta?.skill_taxonomy?.Software || [])];

  const checkDuplicates = async () => {
    if (candidate) return true;
    try {
      const { data } = await api.post("/candidates/check-duplicates", {
        email: form.email, phone: form.phone, full_name: form.full_name,
      });
      setDups(data.duplicates || []);
      return (data.duplicates || []).length === 0;
    } catch {
      return true;
    }
  };

  const submit = async (force = false) => {
    if (!form.full_name.trim()) return toast.error("Candidate name is required");
    if (!force) {
      const clean = await checkDuplicates();
      if (!clean) return toast.warning("Possible duplicate found — review before saving");
    }
    setSaving(true);
    try {
      const payload = { ...form };
      ["id", "candidate_code", "created_at", "updated_at", "created_by", "notes", "documents",
        "recruiter_name", "application_count", "current_stage", "best_match_score", "applications",
        "interviews", "offers", "communications", "activity", "resume_text", "resume_path",
        "resume_filename"].forEach((k) => delete payload[k]);
      const { data } = candidate
        ? await api.put(`/candidates/${candidate.id}`, payload)
        : await api.post("/candidates", payload);
      toast.success(candidate ? "Candidate updated" : `Candidate added · ${data.candidate_code}`);
      onOpenChange(false);
      onSaved?.(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-3xl border-slate-200 bg-white p-0">
        <DialogHeader className="border-b border-slate-200 px-6 py-5">
          <DialogTitle className="text-lg font-medium text-slate-900">
            {candidate ? `Edit ${candidate.full_name}` : "Add a candidate"}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Skills, experience and compensation feed the match engine.
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[calc(92vh-180px)]">
          <div className="space-y-5 px-6 py-5">
            {dups.length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-4" data-testid="duplicate-warning">
                <p className="flex items-center gap-2 text-sm font-medium text-amber-800">
                  <AlertTriangle className="h-4 w-4" /> Possible duplicate records
                </p>
                <ul className="mt-2 space-y-1.5">
                  {dups.map((d) => (
                    <li key={d.id} className="text-xs text-amber-800/80">
                      {d.full_name} · {d.email || "no email"} · {d.reasons.join(", ")}
                    </li>
                  ))}
                </ul>
                <Button
                  size="sm"
                  onClick={() => submit(true)}
                  data-testid="save-anyway-button"
                  className="mt-3 h-7 rounded-full bg-amber-100 text-xs text-amber-800 hover:bg-amber-100"
                >
                  Save as a new candidate anyway
                </Button>
              </div>
            )}

            <SectionTitle>Identity</SectionTitle>
            <FormGrid>
              <TextField label="Full name" value={form.full_name} onChange={set("full_name")} required testid="candidate-name-input" />
              <TextField label="Email" value={form.email} onChange={set("email")} testid="candidate-email-input" />
              <TextField label="Phone" value={form.phone} onChange={set("phone")} testid="candidate-phone-input" />
              <TextField label="Location" value={form.location} onChange={set("location")} placeholder="Bangalore, Karnataka" />
            </FormGrid>

            <SectionTitle>Current role</SectionTitle>
            <FormGrid>
              <TextField label="Current company" value={form.current_company} onChange={set("current_company")} />
              <TextField label="Current designation" value={form.current_designation} onChange={set("current_designation")} />
              <TextField label="Total experience (yrs)" type="number" value={form.total_experience_years} onChange={set("total_experience_years")} testid="candidate-exp-input" />
              <SelectField label="Industry" value={form.industry} onChange={set("industry")} options={Object.keys(meta?.skill_taxonomy || {})} />
            </FormGrid>

            <SectionTitle>Skills &amp; tags</SectionTitle>
            <TagsField label="Skills" values={form.skills} onChange={set("skills")} suggestions={skillPool} testid="candidate-skills-input" />
            <TagsField label="Technologies" values={form.technologies} onChange={set("technologies")} suggestions={skillPool} />
            <TagsField label="Certifications" values={form.certifications} onChange={set("certifications")} />
            <TagsField label="Tags" values={form.tags} onChange={set("tags")} suggestions={meta?.tags || ["Hot", "Referral", "Passive", "Relocation OK"]} />

            <SectionTitle>Compensation &amp; availability</SectionTitle>
            <FormGrid cols={3}>
              <TextField label="Current CTC" value={form.current_ctc} onChange={set("current_ctc")} placeholder="18 LPA" />
              <TextField label="Expected CTC" value={form.expected_ctc} onChange={set("expected_ctc")} placeholder="26 LPA" testid="candidate-expected-ctc" />
              <TextField label="Notice period" value={form.notice_period} onChange={set("notice_period")} />
              <TextField label="Availability" value={form.availability} onChange={set("availability")} />
              <SelectField label="Source" value={form.source} onChange={set("source")} options={meta?.enums?.sources || []} />
              <SelectField label="Status" value={form.status} onChange={set("status")} options={meta?.enums?.candidate_statuses || []} />
            </FormGrid>

            <SectionTitle>Links &amp; ownership</SectionTitle>
            <FormGrid cols={3}>
              <TextField label="LinkedIn" value={form.linkedin} onChange={set("linkedin")} />
              <TextField label="Portfolio" value={form.portfolio} onChange={set("portfolio")} />
              <TextField label="GitHub" value={form.github} onChange={set("github")} />
            </FormGrid>
            <SelectField label="Owning recruiter" value={form.recruiter_id} onChange={set("recruiter_id")}
              options={(meta?.users || []).map((u) => ({ value: u.id, label: u.name }))} testid="candidate-recruiter-select" />
            <TextAreaField label="Profile summary" value={form.summary} onChange={set("summary")} rows={3} />
          </div>
        </ScrollArea>
        <DialogFooter className="border-t border-slate-200 px-6 py-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-500">Cancel</Button>
          <Button onClick={() => submit(false)} disabled={saving} data-testid="candidate-submit-button" className="gap-2 rounded-full bg-blue-600 px-5 hover:bg-blue-500">
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {candidate ? "Save changes" : "Add candidate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
